import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { 
  Package, CheckCircle2, Upload, X, Loader2, 
  ChevronRight, Sparkles, Camera, ArrowRight
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Categorías de productos
const PRODUCT_CATEGORIES = [
  { value: 'telas', label: 'Telas' },
  { value: 'hilos_fibras', label: 'Hilos y Fibras' },
  { value: 'avios', label: 'Avíos y Accesorios' },
  { value: 'etiquetas', label: 'Etiquetas' },
  { value: 'ropa', label: 'Ropa terminada' },
  { value: 'otros', label: 'Otros' }
];

const SellerOnboarding = () => {
  const navigate = useNavigate();
  const { user, token, updateUser } = useAuth();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [products, setProducts] = useState([
    { name: '', category: '', price: '', quantity: '', image: null, imagePreview: null, status: 'draft' },
    { name: '', category: '', price: '', quantity: '', image: null, imagePreview: null, status: 'draft' },
    { name: '', category: '', price: '', quantity: '', image: null, imagePreview: null, status: 'draft' }
  ]);
  const [completedProducts, setCompletedProducts] = useState(0);
  const [initialLoading, setInitialLoading] = useState(true);

  // Check onboarding status on mount
  useEffect(() => {
    const checkOnboardingStatus = async () => {
      if (!token) {
        setInitialLoading(false);
        return;
      }
      
      try {
        const res = await fetch(`${API_URL}/api/onboarding/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (res.ok) {
          const data = await res.json();
          
          // If onboarding completed, redirect to dashboard
          if (data.onboarding_completed) {
            navigate('/dashboard');
            return;
          }
          
          // Load draft products if any
          if (data.draft_products && data.draft_products.length > 0) {
            const loadedProducts = data.draft_products.map(p => ({
              id: p.product_id,
              name: p.name || '',
              category: p.category || '',
              price: p.price || '',
              quantity: p.quantity || '',
              image: null,
              imagePreview: p.images?.[0] || null,
              status: p.status || 'draft'
            }));
            
            // Fill remaining slots
            while (loadedProducts.length < 3) {
              loadedProducts.push({ 
                name: '', category: '', price: '', quantity: '', 
                image: null, imagePreview: null, status: 'draft' 
              });
            }
            
            setProducts(loadedProducts);
            setCompletedProducts(data.completed_products_count || 0);
            
            // Set current step based on completed products
            if (data.completed_products_count >= 1) {
              setCurrentStep(Math.min(data.completed_products_count + 1, 3));
            }
          }
        }
      } catch (e) {
        console.error('Error checking onboarding status:', e);
      } finally {
        setInitialLoading(false);
      }
    };
    
    checkOnboardingStatus();
  }, [token, navigate]);

  // Auto-save draft every 30 seconds
  const saveDraft = useCallback(async (productIndex) => {
    if (!token || saving) return;
    
    const product = products[productIndex];
    if (!product.name.trim()) return;
    
    setSaving(true);
    try {
      const payload = {
        name: product.name,
        category: product.category || 'otros',
        price: product.price ? parseFloat(product.price) : null,
        quantity: product.quantity ? parseInt(product.quantity) : null,
        images: product.imagePreview ? [product.imagePreview] : [],
        status: 'draft',
        step: productIndex + 1
      };
      
      const method = product.id ? 'PUT' : 'POST';
      const url = product.id 
        ? `${API_URL}/api/onboarding/products/${product.id}`
        : `${API_URL}/api/onboarding/products`;
      
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        const data = await res.json();
        const newProducts = [...products];
        newProducts[productIndex].id = data.product_id;
        setProducts(newProducts);
      }
    } catch (e) {
      console.error('Error saving draft:', e);
    } finally {
      setSaving(false);
    }
  }, [token, products, saving]);

  // Debounced auto-save
  useEffect(() => {
    const timer = setTimeout(() => {
      if (products[currentStep - 1]?.name?.trim()) {
        saveDraft(currentStep - 1);
      }
    }, 5000); // Save after 5 seconds of inactivity
    
    return () => clearTimeout(timer);
  }, [products, currentStep, saveDraft]);

  const handleImageUpload = async (file) => {
    if (!file) return;
    
    // Preview local
    const reader = new FileReader();
    reader.onload = async (e) => {
      const newProducts = [...products];
      newProducts[currentStep - 1].imagePreview = e.target.result;
      newProducts[currentStep - 1].image = file;
      setProducts(newProducts);
      
      // Upload to server
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const res = await fetch(`${API_URL}/api/upload/image`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        
        if (res.ok) {
          const data = await res.json();
          const updatedProducts = [...products];
          updatedProducts[currentStep - 1].imagePreview = data.url;
          setProducts(updatedProducts);
        }
      } catch (e) {
        console.error('Error uploading image:', e);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleProductChange = (field, value) => {
    const newProducts = [...products];
    newProducts[currentStep - 1][field] = value;
    setProducts(newProducts);
  };

  const isCurrentProductValid = () => {
    const product = products[currentStep - 1];
    return product.name.trim() && product.category && product.price;
  };

  const handleSaveAndContinue = async () => {
    if (!isCurrentProductValid()) {
      toast.error('Por favor completa los campos requeridos');
      return;
    }
    
    setLoading(true);
    try {
      const product = products[currentStep - 1];
      
      // Upload image if pending
      let imageUrl = product.imagePreview;
      if (product.image && !product.imagePreview?.startsWith('http')) {
        const formData = new FormData();
        formData.append('file', product.image);
        
        const uploadRes = await fetch(`${API_URL}/api/upload/image`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        
        if (uploadRes.ok) {
          const uploadData = await uploadRes.json();
          imageUrl = uploadData.url;
        }
      }
      
      const payload = {
        name: product.name,
        category: product.category,
        price: parseFloat(product.price),
        quantity: product.quantity ? parseInt(product.quantity) : null,
        images: imageUrl ? [imageUrl] : [],
        status: 'active',
        step: currentStep
      };
      
      const method = product.id ? 'PUT' : 'POST';
      const url = product.id 
        ? `${API_URL}/api/onboarding/products/${product.id}`
        : `${API_URL}/api/onboarding/products`;
      
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        const data = await res.json();
        
        // Update product with ID
        const newProducts = [...products];
        newProducts[currentStep - 1].id = data.product_id;
        newProducts[currentStep - 1].status = 'active';
        newProducts[currentStep - 1].imagePreview = imageUrl;
        setProducts(newProducts);
        
        // Update completed count
        const newCompleted = completedProducts + 1;
        setCompletedProducts(newCompleted);
        
        toast.success(`Producto ${currentStep} guardado`);
        
        // Move to next step or complete
        if (currentStep < 3) {
          setCurrentStep(currentStep + 1);
        } else {
          // Complete onboarding
          await completeOnboarding();
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Error al guardar el producto');
      }
    } catch (e) {
      console.error('Error saving product:', e);
      toast.error('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const completeOnboarding = async () => {
    try {
      const res = await fetch(`${API_URL}/api/onboarding/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (res.ok) {
        // Update user context
        if (updateUser) {
          updateUser({ onboarding_completed: true });
        }
        
        // Navigate to success screen
        setCurrentStep(4); // Success step
      }
    } catch (e) {
      console.error('Error completing onboarding:', e);
    }
  };

  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };

  if (initialLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  // Success Screen (Step 4)
  if (currentStep === 4) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white flex flex-col items-center justify-center p-4">
        <div className="w-full max-w-md text-center">
          <div className="w-24 h-24 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
            <CheckCircle2 className="w-12 h-12 text-white" />
          </div>
          
          <h1 className="text-2xl font-bold text-slate-900 mb-3">
            ¡Listo! Tu catálogo ya está activo
          </h1>
          <p className="text-slate-600 mb-8">
            Ahora puedes empezar a recibir cotizaciones
          </p>
          
          {/* Product Summary */}
          <Card className="mb-6 p-4 bg-white shadow-md">
            <p className="text-sm text-slate-500 mb-3">Tus productos:</p>
            <div className="space-y-2">
              {products.filter(p => p.status === 'active').map((p, i) => (
                <div key={i} className="flex items-center gap-3 text-left">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  <span className="text-sm text-slate-700 truncate">{p.name}</span>
                </div>
              ))}
            </div>
          </Card>
          
          <Button 
            className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-6 text-lg"
            onClick={handleGoToDashboard}
            data-testid="go-to-dashboard-btn"
          >
            Ir a mi panel
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  const currentProduct = products[currentStep - 1];
  const progress = (completedProducts / 3) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <div className="sticky top-0 bg-white/95 backdrop-blur-sm border-b z-10 px-4 py-3">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-700">
              Paso {currentStep} de 3
            </span>
            <span className="text-sm text-emerald-600 font-medium">
              {Math.round(progress)}% completado
            </span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>
      </div>

      {/* Content */}
      <div className="max-w-lg mx-auto px-4 py-6">
        {/* Title */}
        <div className="text-center mb-6">
          <h1 className="text-xl font-bold text-slate-900 mb-2">
            Sube tus primeros productos y empieza a recibir clientes
          </h1>
          <p className="text-slate-500 text-sm">
            Solo te tomará 2 minutos
          </p>
        </div>

        {/* Incentive Badge */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6 flex items-center gap-3">
          <Sparkles className="w-5 h-5 text-amber-500 flex-shrink-0" />
          <p className="text-sm text-amber-800">
            <strong>Los usuarios con 3 productos reciben hasta 5x más solicitudes</strong>
          </p>
        </div>

        {/* Progress Indicators */}
        <div className="flex justify-center gap-3 mb-6">
          {[1, 2, 3].map((step) => (
            <div 
              key={step}
              className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all ${
                step < currentStep || products[step - 1].status === 'active'
                  ? 'bg-emerald-500 border-emerald-500 text-white'
                  : step === currentStep
                  ? 'border-emerald-500 text-emerald-500'
                  : 'border-slate-200 text-slate-400'
              }`}
            >
              {step < currentStep || products[step - 1].status === 'active' ? (
                <CheckCircle2 className="w-5 h-5" />
              ) : (
                <span className="font-semibold">{step}</span>
              )}
            </div>
          ))}
        </div>

        {/* Product Form Card */}
        <Card className="shadow-lg">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-slate-900">
                Producto {currentStep}
              </h2>
              {saving && (
                <span className="text-xs text-slate-400 flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Guardando...
                </span>
              )}
            </div>

            {/* Image Upload */}
            <div className="mb-4">
              <Label className="text-sm text-slate-600 mb-2 block">
                Foto del producto <span className="text-slate-400">(recomendado)</span>
              </Label>
              <label className="block w-full h-40 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:border-emerald-500 transition-colors overflow-hidden bg-slate-50">
                {currentProduct.imagePreview ? (
                  <div className="relative w-full h-full">
                    <img 
                      src={currentProduct.imagePreview} 
                      alt="" 
                      className="w-full h-full object-cover" 
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        handleProductChange('imagePreview', null);
                        handleProductChange('image', null);
                      }}
                      className="absolute top-2 right-2 bg-black/50 rounded-full p-1"
                    >
                      <X className="w-4 h-4 text-white" />
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-slate-400">
                    <Camera className="w-8 h-8 mb-2" />
                    <span className="text-sm">Toca para agregar foto</span>
                  </div>
                )}
                <input 
                  type="file" 
                  accept="image/*" 
                  className="hidden"
                  onChange={(e) => handleImageUpload(e.target.files[0])}
                  data-testid={`product-${currentStep}-image`}
                />
              </label>
            </div>

            {/* Product Name */}
            <div className="mb-4">
              <Label className="text-sm text-slate-600 mb-1 block">
                Nombre del producto <span className="text-red-500">*</span>
              </Label>
              <Input 
                value={currentProduct.name}
                onChange={(e) => handleProductChange('name', e.target.value)}
                placeholder="Ej: Tela Gabardina Stretch"
                className="h-12"
                data-testid={`product-${currentStep}-name`}
              />
            </div>

            {/* Category */}
            <div className="mb-4">
              <Label className="text-sm text-slate-600 mb-1 block">
                Tipo de producto <span className="text-red-500">*</span>
              </Label>
              <Select 
                value={currentProduct.category} 
                onValueChange={(v) => handleProductChange('category', v)}
              >
                <SelectTrigger className="h-12" data-testid={`product-${currentStep}-category`}>
                  <SelectValue placeholder="Selecciona categoría" />
                </SelectTrigger>
                <SelectContent>
                  {PRODUCT_CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Price and Quantity Row */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <Label className="text-sm text-slate-600 mb-1 block">
                  Precio <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                  <Input 
                    type="number"
                    value={currentProduct.price}
                    onChange={(e) => handleProductChange('price', e.target.value)}
                    placeholder="0.00"
                    className="h-12 pl-7"
                    data-testid={`product-${currentStep}-price`}
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm text-slate-600 mb-1 block">
                  Cantidad disponible
                </Label>
                <Input 
                  type="number"
                  value={currentProduct.quantity}
                  onChange={(e) => handleProductChange('quantity', e.target.value)}
                  placeholder="Ej: 100"
                  className="h-12"
                  data-testid={`product-${currentStep}-quantity`}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Button */}
        <Button 
          className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-6 text-lg mt-6 shadow-lg"
          onClick={handleSaveAndContinue}
          disabled={!isCurrentProductValid() || loading}
          data-testid={`save-product-${currentStep}-btn`}
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Guardando...
            </>
          ) : currentStep < 3 ? (
            <>
              Guardar y continuar
              <ChevronRight className="w-5 h-5 ml-2" />
            </>
          ) : (
            <>
              Completar y ver mi panel
              <CheckCircle2 className="w-5 h-5 ml-2" />
            </>
          )}
        </Button>

        {/* Skip/Later Option - Only for testing, hidden in production */}
        {process.env.NODE_ENV === 'development' && (
          <button 
            className="w-full text-center text-sm text-slate-400 mt-4 py-2"
            onClick={() => navigate('/dashboard')}
          >
            Saltar (solo desarrollo)
          </button>
        )}
      </div>
    </div>
  );
};

export default SellerOnboarding;
